import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Ciudades } from '../../../Models/ciudades';
import { CreateLead } from '../../../Models/createLead';
import { DatosGenerales } from '../../../Models/datosGenerales';
import { DestinosModels } from '../../../Models/destinos';
import { ExceptionRequestCotizacion } from '../../../Models/exceptionRequestCotizacion';
import { BannerAppComponent } from '../../../components/banner-app/banner-app.component';
import { TermsModalComponent } from '../../../components/common/terms-modal/terms-modal.component';
import { FooterAppComponent } from '../../../components/footer-app/footer-app.component';
import { InfoMensajeComponent } from '../../../components/info-mensaje/info-mensaje.component';
import { getFromStorage, getSessionId, saveToStorage } from '../../../helpers/storageUtils';
import { InitialFormComponent, InitialFormData } from '../../../motos/forms/initial-form/initial-form.component';
import { LOADER, NO_ES_SOAT } from '../../../routes.constans';
import { DataLayerService } from '../../../services/DataLayer/data-layer.service';
import { DestinosService } from '../../../services/Destinos/destinos.service';
import { ExceptionService } from '../../../services/excepciones/exception.service';
import { InactivityService } from '../../../services/inactivity/inactivity.service';
import { UtilsService } from '../../../services/utils.service';
import { WebMPlayerComponent } from '../../../shared/components/web-mplayer/web-mplayer.component';
import { TokenService } from '../../../shared/services/token.service';
import { loadCotizacion } from '../../../storage/Lead/data.actions';
import { selectData } from '../../../storage/Lead/data.selectors';
import {
  createEmptyCiudades,
  createEmptyDatosGenerales,
  createEmptyDestinos,
  createEmptyExceptionRequest,
} from './cotizador.initializers';

@Component({
  selector: 'app-cotizador',
  standalone: true,
  imports: [
    BannerAppComponent,
    FooterAppComponent,
    InfoMensajeComponent,
    CommonModule,
    WebMPlayerComponent,
    InitialFormComponent,
  ],
  templateUrl: './cotizador.component.html',
  styleUrl: './cotizador.component.scss',
})
export class CotizadorComponent implements OnDestroy, OnInit {
  router = inject(Router);
  utils = inject(UtilsService);
  TokenService = inject(TokenService);
  private inactivityTimerStarted = false; // Control para iniciar el timer solo una vez
  private hasLoadedInitialData = false; // Control para evitar re-llenar el formulario desde el store (loop)
  private readonly INITIAL_FORM_STORAGE_KEY = 'initialFormData';
  private readonly USER_STARTED_FILLING_KEY = 'isFormDiligenced';

  @Input() codigoAsesor?: string = '';
  @Input() utm_source?: string = '';
  @Input() utm_medium?: string = '';
  @Input() utm_campaign?: string = '';
  @Input() utm_content?: string = '';
  @Input() utm_term?: string = '';

  // Propiedades para pasar al componente hijo
  states: string[] = [];
  prefilledFormData: InitialFormData | null = null;

  // Inicializaciones usando funciones factory
  ciudades: Ciudades = createEmptyCiudades();
  destinos: DestinosModels = createEmptyDestinos();
  datosGenerales$: Observable<DatosGenerales | null> = this.store.select(selectData);
  datosGenerales: DatosGenerales = createEmptyDatosGenerales();
  envioExcepcionCotizacion: ExceptionRequestCotizacion = createEmptyExceptionRequest();

  sessionId = '';
  enablePromo: boolean = environment.bannerEnabled;
  private startDate: Date = new Date(environment.bannerStartDate);
  private endDate: Date = new Date(environment.bannerEndDate);
  bannerTitleLine1: string = environment.bannerTitleLine1;
  bannerTitleLine2: string = environment.bannerTitleLine2;
  bannerImgDesktop: string = environment.bannerImgDesktop;
  bannerImgMobile: string = environment.bannerImgMobile;

  constructor(
    private destinosServices: DestinosService,
    private store: Store,
    private modalService: NgbModal,
    private dataLayerService: DataLayerService,
    private exceptionService: ExceptionService,
    private inactivityService: InactivityService,
  ) { }

  ngOnInit(): void {
    this.enablePromo = this.isWithinActivePeriod();
    this.sessionId = getSessionId().toString();

    this.dataLayerService.pushDataLayer({
      event: 'moto_ecommerce_step_1_inicio',
      ecom_tipo: 'Moto',
      ecom_item: 'Pageview',
      ecom_pantalla: 'Inicio Moto',
      user_id: this.sessionId,
    });

    const isDiligenced = sessionStorage.getItem(this.USER_STARTED_FILLING_KEY);

    if (!isDiligenced) {

      this.clearAllPersistedData();
    } else {
      const localSavedData = getFromStorage(this.INITIAL_FORM_STORAGE_KEY, 'session') as InitialFormData | null;
      if (localSavedData && (localSavedData.placa || localSavedData.nombreCliente)) {
        this.prefilledFormData = localSavedData;
        this.hasLoadedInitialData = true;
      }
    }

    // Suscribirse al store para actualizar el servicio con datos del lead
    // y preparar datos para pre-llenar el formulario en caso de retoma
    this.datosGenerales$.subscribe(data => {
      if (data) {
        // Clonar datos para evitar referencias inmutables del store
        this.datosGenerales = JSON.parse(JSON.stringify(data));
        this.inactivityService.setLeadData(this.datosGenerales);

        // Preparar datos para pre-llenar formulario si hay datos del lead (retoma)
        // SOLO si no se han cargado ya, para evitar loops infinitos (Form -> Store -> Form -> Loop)
        // Se valida si existe al menos placa, nombre o teléfono para considerar que hay datos de retoma
        const hasRetakeData = data.Lead && (data.Lead.plate || data.Lead.name || data.Lead.mobile_phone);

        if (!this.hasLoadedInitialData && hasRetakeData && sessionStorage.getItem(this.USER_STARTED_FILLING_KEY)) {
          console.log('📝 Preparando datos de retoma para el formulario:', data.Lead);

          this.prefilledFormData = {
            nombreCliente: data.Lead.name || '',
            placa: data.Lead.plate || '',
            confirPlaca: data.Lead.confirm_plate || '',
            celular: data.Lead.mobile_phone || '',
            ciudadCirc: data.Lead.cityName || '',
            tipoUso: data.Lead.use_type || 'particular',
            codigoAsesor: this.codigoAsesor,
            destinoSeleccionado: data.Lead.circulation_zone_id || '',
          };
          console.log('🔄 Formulario pre-llenado con ciudad:', this.prefilledFormData.ciudadCirc);
          saveToStorage(this.INITIAL_FORM_STORAGE_KEY, this.prefilledFormData, 'session');
          this.hasLoadedInitialData = true;
        }
      }
    });

    // Cargar destinos y ciudades
    this.TokenService.subscribeToken().subscribe((token) => {
      if (token) {
        this.destinosServices.getDestinos(this.sessionId).subscribe((data) => {
          if (data !== undefined) {
            const listCiudades = data;
            const vart = JSON.parse(JSON.stringify(listCiudades));
            this.destinos = vart;

            const newStates: string[] = [];
            this.destinos.data.circulation_zones.forEach((itemCiudades) => {
              if (itemCiudades.label !== undefined) {
                newStates.push(itemCiudades.label);
              }
            });
            this.states = newStates;
          }
        });
      }
    });
  }

  private isWithinActivePeriod(): boolean {
    const now = new Date();
    return now >= this.startDate && now <= this.endDate;
  }

  private clearAllPersistedData(): void {
    sessionStorage.removeItem(this.INITIAL_FORM_STORAGE_KEY);
    sessionStorage.removeItem('remainingDataForm');
    sessionStorage.removeItem(this.USER_STARTED_FILLING_KEY);

    this.prefilledFormData = null;
    this.hasLoadedInitialData = false;

    // Resetear el Store con datos vacíos para evitar que arrastre leads viejos
    const emptyState = createEmptyDatosGenerales();
    this.store.dispatch(loadCotizacion({ data: emptyState }));
  }

  /**
   * Handler cuando los campos críticos del formulario están completos
   * Inicia el timer de inactividad
   */
  onCriticalFieldsComplete(formData: InitialFormData): void {
    console.log('✅ Campos críticos completos. Actualizando datos...', formData);

    saveToStorage(this.INITIAL_FORM_STORAGE_KEY, formData, 'session');

    // Guardar los datos del formulario en datosGenerales (clonando para evitar mutación de store)
    this.datosGenerales = {
      ...this.datosGenerales,
      Lead: {
        ...this.datosGenerales.Lead,
        name: formData.nombreCliente,
        plate: formData.placa,
        confirm_plate: formData.confirPlaca,
        mobile_phone: formData.celular,
        cityName: formData.ciudadCirc,
        circulation_zone_id: formData.destinoSeleccionado,
        use_type: formData.tipoUso,
        accept_tos: true,
        assessor_code: Number(formData.codigoAsesor) ?? 0,
      }
    };

    // Dispatch to Store to ensure persistence if user navigates away (e.g. Inactivity Pause)
    this.store.dispatch(loadCotizacion({ data: this.datosGenerales }));

    this.inactivityService.setLeadData(this.datosGenerales);

    // Iniciar el timer solo si no ha iniciado aún
    if (!this.inactivityTimerStarted) {
      this.inactivityService.startDefaultTimerForScreen(
        'Ingreso-cotizador',
        'Abandono Ingreso a cotizador',
        '5'
      );
      this.inactivityTimerStarted = true;
    }

    sessionStorage.setItem(this.USER_STARTED_FILLING_KEY, 'true');
  }

  /**
   * Handler cuando se envía el formulario (botón Cotizar)
   */
  onFormSubmit(formData: InitialFormData): void {

    saveToStorage(this.INITIAL_FORM_STORAGE_KEY, formData, 'session');
    sessionStorage.setItem(this.USER_STARTED_FILLING_KEY, 'true');

    const datosLead: CreateLead = {
      assessor_code: Number(formData.codigoAsesor) ?? 0,
      plate: formData.placa,
      confirm_plate: formData.confirPlaca,
      circulation_zone_id: formData.destinoSeleccionado,
      cityName: formData.ciudadCirc,
      use_type: formData.tipoUso,
      accept_tos: true,
      name: formData.nombreCliente,
      mobile_phone: formData.celular,
    };

    this.datosGenerales = {
      ...this.datosGenerales,
      Lead: datosLead
    };
    this.store.dispatch(loadCotizacion({ data: this.datosGenerales }));

    this.utils.handleredirect(this.codigoAsesor ?? '', LOADER);

    this.dataLayerService.pushDataLayer({
      event: 'moto_ecommerce_step_1_inicio',
      ecom_tipo: 'Moto',
      ecom_item: 'Button',
      ecom_subitem: 'Cotizar',
      ecom_pantalla: 'Inicio Moto',
      user_id: this.sessionId,
      zona_circulacion: formData.ciudadCirc,
      uso_vehiculo: formData.tipoUso,
      placa: formData.placa || '',
    });

    if (this.codigoAsesor === undefined || this.codigoAsesor === '') {
      this.router.navigate([NO_ES_SOAT]);
    } else {
      this.router.navigate([NO_ES_SOAT], {
        queryParams: { codigoAsesor: this.codigoAsesor },
      });
    }
  }

  /**
   * Handler cuando se selecciona un asesor
   */
  onAdvisorSelected(advisor: any): void {
    if (advisor && advisor.Cod) {
      this.codigoAsesor = advisor.Cod;
      this.envioExcepcionCotizacion.assessor_code = advisor.Cod;
    }
  }

  /**
   * Handler para abrir modal de términos y condiciones
   */
  onTermsModalRequested(): void {
    const modalRef = this.modalService.open(TermsModalComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.title = 'Terminos y Condiciones';

    modalRef.componentInstance.close.subscribe(() => {
      modalRef.close();
    });
  }

  ngOnDestroy(): void {
    this.inactivityService.stopTimer();
  }
}
